import { Toaster as SonnerToaster } from 'sonner';

/**
 * Replaces MatSnackBar.
 *
 * POSITION — an intentional behaviour change, recorded in BEHAVIOUR-CHANGES.md:
 * `notification.service.ts` asks for `verticalPosition: 'top'`, but styles.scss:154
 * forces `align-items: center` on the CDK overlay wrapper via
 * `:has(.mat-mdc-snack-bar-container)`, so today's toasts render in the dead centre
 * of the viewport, directly over the form the user is filling in. That placement was
 * an accident of the `:has()` rule, not a design decision, so we honour the code's
 * stated intent and render top-centre.
 *
 * COLOURS are the stock Material palette hexes the `*-snackbar` panel classes used
 * (styles.scss:160-263). They were never tokenised — `.success-snackbar` is `#4caf50`,
 * not `--success-color` (`#1d6f42`). Reproduced as literals to stay pixel-faithful;
 * retokenising them would be a visible change and belongs in a separate decision.
 */
export function Toaster() {
  return (
    <SonnerToaster
      position="top-center"
      expand={false}
      visibleToasts={3}
      gap={8}
      closeButton
      toastOptions={{
        unstyled: true,
        classNames: {
          /*
           * ONE FIXED WIDTH for every toast, asked for directly. It used to size to its message
           * (300–560px), so a short success toast stacked narrower than the error under it and the
           * column looked ragged. A long message now wraps instead of widening its toast; the
           * `max-w` keeps it on screen on a narrow viewport.
           */
          toast:
            'relative flex items-start gap-3 w-[360px] max-w-[calc(100vw-2rem)] px-4 py-3 rounded-sm ' +
            'text-base font-medium text-white shadow-modal',
          /*
           * `whitespace-pre-line` so a message carrying newlines renders as separate lines rather
           * than one run-on paragraph. `notify.error` puts them there; see `splitMessageLines`.
           */
          title: 'flex-1 pr-6 whitespace-pre-line text-left leading-5',
          success: 'bg-[#4caf50]',
          error: 'bg-[#f44336]',
          warning: 'bg-[#ff9800]',
          info: 'bg-[#2196f3]',
          loading: 'bg-[#424242]',
          closeButton:
            'absolute top-3 right-3 flex items-center justify-center border-0 bg-transparent p-0 ' +
            'cursor-pointer text-white/80 hover:text-white',
        },
      }}
    />
  );
}
